import React, { useState } from 'react';
import { notify } from '../../../../utils/toast';
import { ArrowLeftIcon } from '../../../../components/icons';
import { Button } from '../../../../components/ui/Button';
import { Card } from '../../../../components/ui/Card';
import Input from '../../../../components/ui/Input';

interface CreateConnectionFormProps {
    onBack: () => void;
    onSubmit: (name: string, phoneNumber: string) => void;
    isLoading: boolean;
    error: string | null;
    /** Sem chrome de card/título — uso em onboarding (evita card dentro de card). */
    embedded?: boolean;
}

const CreateConnectionForm: React.FC<CreateConnectionFormProps> = ({
    onBack,
    onSubmit,
    isLoading,
    error,
    embedded = false,
}) => {
    const [name, setName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('+55');

    const handleSubmit = (useNumber: boolean) => {
        if (!name.trim()) {
            notify.warning('Por favor, digite um nome para a conexão.');
            return;
        }
        onSubmit(name, useNumber ? phoneNumber : '');
    };

    const fields = (
        <>
            <Input
                id="connection-name"
                label="Nome da conexão"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Vendas, Suporte, Meu WhatsApp"
            />
            <Input
                id="connection-phone"
                label="Número"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+55 11 99999-9999"
                className="font-mono"
            />
            <div
                className={
                    embedded
                        ? 'flex justify-start pt-s1'
                        : 'flex flex-col-reverse gap-s2 pt-s1 sm:flex-row sm:justify-end sm:gap-s3'
                }
            >
                {!embedded && (
                    <Button
                        type="button"
                        variant="outline"
                        className="min-h-11 w-full touch-manipulation sm:w-auto"
                        onClick={() => handleSubmit(false)}
                        disabled={isLoading}
                    >
                        Conectar sem selecionar número
                    </Button>
                )}
                <Button
                    type="button"
                    className={
                        embedded
                            ? 'min-h-11 w-full touch-manipulation sm:w-auto'
                            : 'min-h-11 w-full touch-manipulation sm:w-auto sm:px-s8'
                    }
                    onClick={() => handleSubmit(true)}
                    disabled={isLoading}
                >
                    {isLoading ? 'Criando...' : 'Criar conexão'}
                </Button>
            </div>
        </>
    );

    return (
        <div className={embedded ? 'flex flex-col gap-s4' : 'flex flex-col gap-s5'}>
            {!embedded && (
                <div className="flex items-center gap-s3">
                    <button
                        type="button"
                        onClick={onBack}
                        className="rounded-lg p-s1 text-muted transition-colors hover:bg-surface-subtle hover:text-default"
                        aria-label="Voltar"
                    >
                        <ArrowLeftIcon className="size-5" />
                    </button>
                    <h2 className="text-lg font-semibold text-default sm:text-xl">Nova conexão</h2>
                </div>
            )}

            {embedded ? (
                <div className="flex flex-col gap-s4">{fields}</div>
            ) : (
                <Card className="flex flex-col gap-s4 border-none bg-surface p-s6 shadow-sm">{fields}</Card>
            )}

            {error && (
                <p
                    role="alert"
                    className="rounded-lg border border-danger/20 bg-danger/10 px-s4 py-s3 text-sm text-danger"
                >
                    {error}
                </p>
            )}
        </div>
    );
};

export default CreateConnectionForm;
